// ConfigDefaultsPanel — UI for editing the platform's strategy + routing
// defaults at runtime. Renders inside SettingsModal.
//
// Two distinct sections:
//   1. Strategy defaults — for each (execution_mode × provider class) row,
//      pick a strategy + agent model. Resolves on every run unless the
//      ticket explicitly sets agent_strategy / agent_model.
//   2. Routing defaults — for each task type in provider-config.yml's
//      `routing` map, pick a (provider, model). code_generation also has a
//      red_zone variant. Reviewer / auditor / classifier all read from
//      these.
//
// Save semantics: we send the FULL overlay (baseline-vs-effective deltas)
// in a PATCH each time. Server validates, replaces the runtime_config row,
// returns the new effective config. Next run picks it up — no rebuild, no
// restart.

import { useEffect, useMemo, useState } from 'react';
import * as api from '../lib/api';
import type {
  AgentStrategy,
  ConfigDefaultsResponse,
  ExecutionMode,
  ProviderClass,
  RouteTarget,
  RouterConfig,
  StrategyDefault,
  TaskType,
} from '../lib/types';

const EXECUTION_MODES: ExecutionMode[] = ['autonomous', 'assisted'];
const PROVIDER_CLASSES: ProviderClass[] = ['anthropic', 'openai', 'openrouter'];

const TASK_TYPES: TaskType[] = [
  'code_generation',
  'code_review',
  'audit',
  'zone_classification',
  'impact_analysis',
  'test_generation',
  'playwright_generation',
  'context_assembly',
];

// Editable view-model: a flat shape that mirrors the routing + strategy_defaults
// surface but uses string keys so React state updates are easy to compose.
interface EditorState {
  strategy: Record<string, StrategyDefault>;     // key: `${mode}:${class}`
  routing: Record<string, RouteTarget>;          // key: `${task}:default` or `${task}:red_zone`
}

function strategyKey(mode: ExecutionMode, cls: ProviderClass): string {
  return `${mode}:${cls}`;
}

function routingKey(task: TaskType, variant: 'default' | 'red_zone'): string {
  return `${task}:${variant}`;
}

function configToEditor(cfg: RouterConfig): EditorState {
  const strategy: Record<string, StrategyDefault> = {};
  const routing: Record<string, RouteTarget> = {};

  for (const mode of EXECUTION_MODES) {
    for (const cls of PROVIDER_CLASSES) {
      const entry = cfg.strategy_defaults?.[mode]?.[cls];
      if (entry) strategy[strategyKey(mode, cls)] = { ...entry };
    }
  }

  for (const task of TASK_TYPES) {
    const r = cfg.routing[task];
    if (!r) continue;
    routing[routingKey(task, 'default')] = { ...r.default };
    if (r.red_zone) routing[routingKey(task, 'red_zone')] = { ...r.red_zone };
  }

  return { strategy, routing };
}

/**
 * Compute the overlay to PATCH: include only entries that differ from the
 * baseline. Entries that match baseline are omitted so the overlay stays
 * minimal — that way reverting an edit is just deleting it from state.
 */
function diffEditorAgainstBaseline(
  edited: EditorState,
  baseline: RouterConfig,
): Record<string, unknown> {
  const overlay: Record<string, unknown> = {};

  // strategy_defaults: include every set entry (the YAML doesn't ship one;
  // anything in the editor came from the overlay or from a fresh user edit).
  const strategyOverlay: Record<string, Record<string, StrategyDefault>> = {};
  for (const [key, value] of Object.entries(edited.strategy)) {
    const [mode, cls] = key.split(':') as [ExecutionMode, ProviderClass];
    strategyOverlay[mode] ??= {};
    strategyOverlay[mode][cls] = value;
  }
  if (Object.keys(strategyOverlay).length > 0) {
    overlay['strategy_defaults'] = strategyOverlay;
  }

  // routing: include only entries that differ from the YAML baseline.
  const routingOverlay: Record<string, Record<string, RouteTarget>> = {};
  for (const task of TASK_TYPES) {
    const r = baseline.routing[task];
    if (!r) continue;
    const editedDefault = edited.routing[routingKey(task, 'default')];
    if (editedDefault && (
      editedDefault.provider !== r.default.provider ||
      editedDefault.model !== r.default.model
    )) {
      routingOverlay[task] ??= {};
      routingOverlay[task]['default'] = editedDefault;
    }
    const editedRed = edited.routing[routingKey(task, 'red_zone')];
    if (editedRed && r.red_zone && (
      editedRed.provider !== r.red_zone.provider ||
      editedRed.model !== r.red_zone.model
    )) {
      routingOverlay[task] ??= {};
      routingOverlay[task]['red_zone'] = editedRed;
    }
  }
  if (Object.keys(routingOverlay).length > 0) {
    overlay['routing'] = routingOverlay;
  }

  return overlay;
}

function modelsForProvider(payload: ConfigDefaultsResponse, provider: string): string[] {
  return (payload.available_models[provider] ?? []).map((m) => m.alias);
}

export function ConfigDefaultsPanel(props: {
  onToast: (msg: string, tone?: 'info' | 'warn' | 'error') => void;
}) {
  const [payload, setPayload] = useState<ConfigDefaultsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.getConfigDefaults();
        if (cancelled) return;
        setPayload(res);
        setEditor(configToEditor(res.effective));
      } catch (err) {
        if (cancelled) return;
        setLoadError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = useMemo(() => {
    if (!payload || !editor) return false;
    const overlay = diffEditorAgainstBaseline(editor, payload.baseline);
    return JSON.stringify(overlay) !== JSON.stringify(payload.overlay);
  }, [payload, editor]);

  const onSave = async (): Promise<void> => {
    if (!payload || !editor) return;
    const overlay = diffEditorAgainstBaseline(editor, payload.baseline);
    setSaving(true);
    try {
      const next = await api.updateConfigDefaults(overlay);
      setPayload(next);
      setEditor(configToEditor(next.effective));
      props.onToast('config defaults saved — next run will pick them up', 'info');
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      props.onToast(`save failed: ${message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const onReset = (): void => {
    if (!payload) return;
    setEditor(configToEditor(payload.baseline));
    props.onToast('reverted to provider-config.yml baseline (not yet saved)', 'info');
  };

  if (loadError) {
    return (
      <section>
        <h4>strategy &amp; routing defaults</h4>
        <div className="kv-row"><span className="mono dim">error: {loadError}</span></div>
      </section>
    );
  }
  if (!payload || !editor) {
    return (
      <section>
        <h4>strategy &amp; routing defaults</h4>
        <div className="kv-row"><span className="mono dim">loading…</span></div>
      </section>
    );
  }

  const setStrategyEntry = (
    mode: ExecutionMode,
    cls: ProviderClass,
    next: StrategyDefault,
  ): void => {
    setEditor((prev) => prev && ({
      ...prev,
      strategy: { ...prev.strategy, [strategyKey(mode, cls)]: next },
    }));
  };

  const setRoutingEntry = (
    task: TaskType,
    variant: 'default' | 'red_zone',
    next: RouteTarget,
  ): void => {
    setEditor((prev) => prev && ({
      ...prev,
      routing: { ...prev.routing, [routingKey(task, variant)]: next },
    }));
  };

  return (
    <>
      <section>
        <h4>strategy defaults</h4>
        <div className="kv-row dim mono" style={{ marginBottom: 6, fontSize: 10.5 }}>
          execution-mode × provider-class → which strategy + model the orchestrator
          picks when the ticket leaves <code>agent_strategy</code> unset.
        </div>
        {EXECUTION_MODES.map((mode) =>
          PROVIDER_CLASSES.map((cls) => {
            const entry = editor.strategy[strategyKey(mode, cls)];
            return (
              <StrategyRow
                key={`${mode}:${cls}`}
                mode={mode}
                cls={cls}
                value={entry}
                strategies={payload.strategies}
                models={modelsForProvider(payload, cls)}
                onChange={(next) => setStrategyEntry(mode, cls, next)}
              />
            );
          }),
        )}
      </section>

      <section>
        <h4>routing defaults</h4>
        <div className="kv-row dim mono" style={{ marginBottom: 6, fontSize: 10.5 }}>
          per-purpose provider + model. Reviewer, auditor, zone classifier all
          read from this map.
        </div>
        {TASK_TYPES.map((task) => (
          <RoutingRows
            key={task}
            task={task}
            payload={payload}
            editor={editor}
            onChange={setRoutingEntry}
          />
        ))}
      </section>

      <section>
        <div className="kv-row" style={{ justifyContent: 'flex-end', gap: 8 }}>
          <button className="drafts-action" onClick={onReset} disabled={saving}>
            reset to baseline
          </button>
          <button
            className="drafts-action primary"
            onClick={() => { void onSave(); }}
            disabled={!dirty || saving}
          >
            {saving ? 'saving…' : 'save changes'}
          </button>
        </div>
        <div className="kv-row dim mono" style={{ fontSize: 10.5 }}>
          {payload.updated_at && payload.updated_at !== '1970-01-01T00:00:00.000Z' ? (
            <span>last saved {new Date(payload.updated_at).toLocaleString()}{payload.updated_by ? ` by ${payload.updated_by}` : ''}</span>
          ) : (
            <span>no overlay applied — using provider-config.yml baseline</span>
          )}
        </div>
      </section>
    </>
  );
}

function StrategyRow(props: {
  mode: ExecutionMode;
  cls: ProviderClass;
  value: StrategyDefault | undefined;
  strategies: AgentStrategy[];
  models: string[];
  onChange: (next: StrategyDefault) => void;
}) {
  // No prior value → seed from the first strategy + first model so the row
  // is always editable. The user can still pick "use baseline" by clearing
  // (no-op for v1; defer.)
  const value = props.value ?? {
    strategy: props.strategies[0] ?? 'claude-agent-sdk',
    model: props.models[0] ?? '',
  };
  return (
    <div className="kv-row" style={{ gap: 8, alignItems: 'center' }}>
      <span className="mono" style={{ minWidth: 180 }}>
        {props.mode} × {props.cls}
      </span>
      <select
        className="mono"
        value={value.strategy}
        onChange={(e) => props.onChange({ ...value, strategy: e.target.value })}
        aria-label={`${props.mode} ${props.cls} strategy`}
      >
        {props.strategies.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>
      <select
        className="mono"
        value={value.model ?? ''}
        onChange={(e) => props.onChange({ ...value, model: e.target.value })}
        aria-label={`${props.mode} ${props.cls} model`}
        disabled={props.models.length === 0}
      >
        {props.models.length === 0 && <option value="">(no models — provider not in YAML)</option>}
        {props.models.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
}

function RoutingRows(props: {
  task: TaskType;
  payload: ConfigDefaultsResponse;
  editor: EditorState;
  onChange: (task: TaskType, variant: 'default' | 'red_zone', next: RouteTarget) => void;
}) {
  const baseRouting = props.payload.baseline.routing[props.task];
  if (!baseRouting) return null;
  const defaultEntry = props.editor.routing[routingKey(props.task, 'default')]
    ?? baseRouting.default;
  const redEntry = baseRouting.red_zone
    ? props.editor.routing[routingKey(props.task, 'red_zone')] ?? baseRouting.red_zone
    : null;

  return (
    <>
      <RoutingRow
        label={props.task}
        value={defaultEntry}
        payload={props.payload}
        onChange={(next) => props.onChange(props.task, 'default', next)}
      />
      {redEntry && (
        <RoutingRow
          label={`${props.task} (red zone)`}
          value={redEntry}
          payload={props.payload}
          onChange={(next) => props.onChange(props.task, 'red_zone', next)}
        />
      )}
    </>
  );
}

function RoutingRow(props: {
  label: string;
  value: RouteTarget;
  payload: ConfigDefaultsResponse;
  onChange: (next: RouteTarget) => void;
}) {
  const models = modelsForProvider(props.payload, props.value.provider);
  return (
    <div className="kv-row" style={{ gap: 8, alignItems: 'center' }}>
      <span className="mono" style={{ minWidth: 200 }}>{props.label}</span>
      <select
        className="mono"
        value={props.value.provider}
        onChange={(e) => {
          const provider = e.target.value;
          const newModels = modelsForProvider(props.payload, provider);
          // Snap model to the first available alias on the new provider so
          // we never leave the row in an invalid (provider, model) state —
          // server validates on save and would reject otherwise.
          const model = newModels[0] ?? props.value.model;
          props.onChange({ provider, model });
        }}
        aria-label={`${props.label} provider`}
      >
        {props.payload.available_providers.map((p) => (
          <option key={p} value={p}>{p}</option>
        ))}
      </select>
      <select
        className="mono"
        value={props.value.model}
        onChange={(e) => props.onChange({ ...props.value, model: e.target.value })}
        aria-label={`${props.label} model`}
      >
        {models.length === 0 && <option value="">(no models)</option>}
        {models.map((m) => <option key={m} value={m}>{m}</option>)}
      </select>
    </div>
  );
}
